import { friendlyError } from "@/lib/friendly-errors";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/** useLayoutEffect on the client, no-op during SSR (avoids the React warning). */
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;
import { supabase } from "@/integrations/supabase/client";
import { isBrokeredPreviewAuth, readStoredAuthSession } from "@/lib/auth-session";

export type MembershipStatus = {
  loading: boolean;
  isAuthed: boolean;
  isMember: boolean;
  isAdmin: boolean;
  tier: "guest" | "free" | "healing";
  displayName: string;
  /**
   * Populated when auth/profile resolution hangs or errors. Callers should
   * surface this to the user with a retry affordance instead of leaving a
   * silent spinner on screen.
   */
  error: string | null;
  /**
   * False on the server render and on the very first client render (which
   * must match it). Guards that redirect signed-out visitors MUST wait for
   * this, otherwise a mount-time effect sees the guest snapshot and bounces
   * a signed-in member to /auth before the seeded session is promoted.
   */
  hydrated: boolean;
  /**
   * True only once auth has been DEFINITIVELY determined — a real session was
   * seen, or Supabase confirmed there is none. A timeout, a slow refresh or a
   * network wedge leaves this false: "not confirmed yet" is never "signed
   * out". No protected page may redirect to sign-in while this is false.
   */
  authResolved: boolean;
  /**
   * True when a signed-out redirect is definitely safe: hydrated, auth
   * resolved, no session, and no unresolved error. Every protected route
   * should use this single flag rather than re-deriving the rules.
   */
  canRedirectToSignIn: boolean;
  /**
   * Re-runs membership verification with a fresh timeout. Safe to call from
   * event handlers (e.g. a "Try again" button on the /membership error card).
   * No-ops when the viewer is not authenticated.
   */
  retry: () => void;
};

const GUEST_BASE = {
  loading: false,
  isAuthed: false,
  isMember: false,
  isAdmin: false,
  tier: "guest" as const,
  displayName: "friend",
  error: null,
};

// Max time we're willing to wait for the profile lookup + has_role RPC.
// Both are cheap PostgREST calls; anything past this window means the auth
// client is stuck (Web-Locks contention, network wedge, etc.) and we should
// unblock the UI with an actionable error instead of spinning forever.
const RESOLVE_TIMEOUT_MS = 6_000;

/**
 * Postgres renders timestamptz offsets as `+00`, which `new Date()` parses as
 * NaN in some engines. Normalise to `+00:00` so an access window that is
 * genuinely in the future is never read as expired (which would lock a paid
 * or complimentary member out of members-only pages).
 */
function isFutureTimestamp(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalised = value.replace(/([+-]\d{2})$/, "$1:00").replace(" ", "T");
  const ms = new Date(normalised).getTime();
  return Number.isFinite(ms) && ms > Date.now();
}



function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/**
 * Resolves the current viewer's membership status.
 *
 * Seeds synchronously from the root-restored auth session (localStorage) and
 * reacts to `supabase.auth.onAuthStateChange` and the root's
 * `rt-auth-change` broadcast. Never calls `supabase.auth.getUser()` — that
 * call can deadlock on Web-Locks contention. Profile + role lookups are
 * bounded by a timeout so a hung backend surfaces an error rather than
 * leaving the UI on a spinner.
 */
export function useMembership(): MembershipStatus {
  const initial = typeof window === "undefined" ? null : readStoredAuthSession();
  type InternalStatus = Omit<
    MembershipStatus,
    "retry" | "hydrated" | "authResolved" | "canRedirectToSignIn"
  >;
  // The very first client render MUST match the server render, otherwise
  // React discards the hydrated tree and the whole page (Recovery Library,
  // Learn Hub, Self-Assessments) tears down and redraws — which is what
  // produced the "This page didn't load" screen on mobile. So we always
  // start from the guest snapshot the server rendered, then switch to the
  // seeded signed-in state in a layout effect (before paint, so there is no
  // visible guest flash).
  const [state, setState] = useState<InternalStatus>(() => ({ ...GUEST_BASE }));
  const [hydrated, setHydrated] = useState(false);
  // Auth is "resolved" only when a real session was seen, or Supabase
  // confirmed there is none. A timeout never resolves it.
  const [authResolved, setAuthResolved] = useState(false);
  const authResolvedRef = useRef(false);
  const markAuthResolved = useCallback(() => {
    if (authResolvedRef.current) return;
    authResolvedRef.current = true;
    setAuthResolved(true);
  }, []);
  useIsomorphicLayoutEffect(() => {
    setHydrated(true);
    const uid = initial?.user?.id;
    // A stored, unexpired session is definitive proof of a signed-in member.
    if (uid) markAuthResolved();
    setState((prev) =>
      uid
        ? {
            loading: true,
            isAuthed: true,
            isMember: false,
            isAdmin: false,
            tier: "free",
            displayName: "friend",
            error: null,
          }
        : prev.isAuthed
          ? prev
          : { ...GUEST_BASE, loading: true },
    );
    // Seeded session id is stable for the life of the mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const lastUidRef = useRef<string | null>(initial?.user?.id ?? null);
  // Latest hydrate function — `retry` reads through this ref so it can be
  // called from event handlers (e.g. the /membership Try again button)
  // without capturing a stale closure.
  const hydrateRef = useRef<(uid: string | null) => Promise<void>>(async () => {});

  useEffect(() => {
    let cancelled = false;
    let authResolved = false;

    // Live lock/unlock: the Stripe and PayPal webhooks write the membership
    // row (cancelled on a cancel event, active on payment). Watching that row
    // means a cancellation revokes access in the open tab immediately — no
    // reload, no waiting for the next navigation.
    let membershipChannel: ReturnType<typeof supabase.channel> | null = null;
    let subscribedUid: string | null = null;
    const dropMembershipChannel = () => {
      if (!membershipChannel) return;
      try {
        void supabase.removeChannel(membershipChannel);
      } catch {
        /* realtime unavailable — nothing to tear down */
      }
      membershipChannel = null;
      subscribedUid = null;
    };
    const subscribeToMembershipRow = (uid: string) => {
      if (subscribedUid === uid && membershipChannel) return;
      dropMembershipChannel();
      if (typeof supabase.channel !== "function") return;
      try {
        subscribedUid = uid;
        membershipChannel = supabase
          .channel(`membership-row-${uid}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "memberships",
              filter: `user_id=eq.${uid}`,
            },
            (payload) => {
              if (cancelled || lastUidRef.current !== uid) return;
              const next = (payload.new ?? {}) as { status?: string | null };
              console.debug("[membership.verify] membership row changed", {
                status: next.status ?? null,
                uidHash: uid.slice(0, 8),
              });
              void hydrate(uid);
            },
          )
          .subscribe();
      } catch {
        // Realtime not available in this environment — the normal
        // hydrate-on-auth-change path still applies the cancellation.
        membershipChannel = null;
        subscribedUid = null;
      }
    };


    async function hydrate(uid: string | null) {
      if (!uid) {
        lastUidRef.current = null;
        dropMembershipChannel();
        if (!cancelled) setState({ ...GUEST_BASE });
        return;
      }

      lastUidRef.current = uid;
      subscribeToMembershipRow(uid);

      // Immediately unblock the UI with the seeded session. Do not wait on
      // supabase reads — enrich with profile/role data as it resolves.
      if (!cancelled)
        setState((prev) =>
          prev.isAuthed && !prev.error
            ? prev
            : {
                loading: true,
                isAuthed: true,
                isMember: false,
                isAdmin: false,
                              tier: "free",
                displayName: "friend",
                error: null,
              },
        );

      // Correlation id ties the attempt log entries together across the
      // start/success/timeout/error events so regressions can be traced.
      const correlationId =
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `mv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);
      const rpcs = ["profiles.select", "rpc:has_role"] as const;
      const startedAt =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const elapsed = () =>
        Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
            startedAt,
        );
      const emit = (payload: Record<string, unknown>) => {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("rt-membership-verify", { detail: payload }),
          );
        }
      };
      // Unified structured logger — every outcome (success, timeout, error)
      // ships the same shape so log collectors and dashboards can group by
      // `outcome` without special-casing. `success` goes through
      // console.debug to stay out of the default warning stream; failures
      // stay on console.warn so they surface in normal debugging.
      const log = (
        outcome: "success" | "timeout" | "error",
        extra: Record<string, unknown> = {},
      ) => {
        const entry = {
          scope: "membership.verify",
          outcome,
          correlationId,
          rpcs,
          timeoutMs: RESOLVE_TIMEOUT_MS,
          elapsedMs: elapsed(),
          uidHash: uid.slice(0, 8),
          ...extra,
        };
        if (outcome === "success") {
          console.debug("[membership.verify]", entry);
        } else {
          console.warn("[membership.verify]", entry);
        }
        emit(entry);
      };

      try {
        // Fire all three requests in parallel. Admin short-circuits early;
        // a paid/complimentary active membership row is the source of truth
        // for `isMember` — never grant it from just being signed in or from a
        // stale active status without payment evidence.
        const profilePromise = supabase
          .from("profiles")
          .select("membership_tier, display_name")
          .eq("user_id", uid)
          .maybeSingle();
        const rolePromise = supabase.rpc("has_role", {
          _user_id: uid,
          _role: "admin",
        });
        const membershipPromise = supabase
          .from("memberships")
          .select("status, paid_at, amount_paid_cents, stripe_subscription_id, complimentary_grant, current_period_end")
          .eq("user_id", uid)
          .maybeSingle();
        // Time-limited passes (e.g. redeemed Gift Memberships) also grant access.
        const passPromise = Promise.resolve(
          supabase.rpc("has_active_recovery_pass", { _user_id: uid }),
        ).then((r) => !r.error && !!r.data).catch(() => false);


        let adminShortCircuited = false;
        const adminRace = rolePromise.then((roleRes) => {
          if (cancelled || lastUidRef.current !== uid) return;
          if (!roleRes.error && roleRes.data) {
            adminShortCircuited = true;
            setState((prev) => ({
              ...prev,
              loading: false,
              isAuthed: true,
              isAdmin: true,
              isMember: true,
                          tier: "healing",
              error: null,
            }));
            log("success", { shortCircuit: "admin" });
          }
        });

        const [profileRes, roleRes, membershipRes, hasPass] = await withTimeout(
          Promise.all([
            profilePromise,
            rolePromise,
            membershipPromise,
            adminRace,
            passPromise,
          ]).then(([p, r, m, _a, pass]) => [p, r, m, pass] as const),
          RESOLVE_TIMEOUT_MS,
          "Membership resolution",
        );
        if (cancelled || lastUidRef.current !== uid) return;
        const profile = profileRes.data;
        const isAdmin = !roleRes.error && !!roleRes.data;
        const membershipRow = membershipRes.data as {
          status?: string | null;
          paid_at?: string | null;
          amount_paid_cents?: number | null;
          stripe_subscription_id?: string | null;
          complimentary_grant?: unknown | null;
          current_period_end?: string | null;
        } | null;
        const membershipStatus = membershipRow?.status ?? null;
        // A complimentary grant only counts while it is still running — an
        // expired grant must not keep access open (mirrors is_founding_member).
        const grant = membershipRow?.complimentary_grant as
          | { expires_at?: string | null }
          | null
          | undefined;
        const hasLiveComplimentaryGrant =
          !!grant &&
          (!grant.expires_at || isFutureTimestamp(grant.expires_at));
        const hasPaidEvidence =
          hasLiveComplimentaryGrant ||
          (!!membershipRow?.paid_at &&
            (!!membershipRow?.stripe_subscription_id ||
              (membershipRow?.amount_paid_cents ?? 0) > 0));
        // A member whose latest charge failed keeps access until the end of
        // the period they already paid for. This mirrors the server-side
        // `is_founding_member` grace rule exactly — without it, a paid member
        // is bounced back to checkout while the backend says they're in.
        const inPaidPeriod = isFutureTimestamp(membershipRow?.current_period_end);
        const hasActiveMembership =
          (membershipStatus === "active" ||
            membershipStatus === "active_member" ||
            (membershipStatus === "payment_failed" && inPaidPeriod)) &&
          hasPaidEvidence;

        const isMember = isAdmin || hasActiveMembership || hasPass;
        const tier =
          (profile?.membership_tier as "free" | "healing" | undefined) ?? "free";
        setState({
          loading: false,
          isAuthed: true,
          isAdmin,
          isMember,
          tier: isAdmin ? "healing" : isMember ? tier : "free",
          displayName: /^apple reviewer$/i.test(profile?.display_name?.trim() ?? "")
            ? "Apple Reviewer"
            : profile?.display_name?.split(" ")[0] || "friend",
          error: null,
        });
        if (!adminShortCircuited) {
          log("success", { membershipStatus, isMember, hasActiveMembership, hasPaidEvidence });
        }
      } catch (e) {
        if (cancelled || lastUidRef.current !== uid) return;
        const message =
          e instanceof Error ? e.message : "Could not verify your membership.";
        const outcome: "timeout" | "error" = /timed out/i.test(message)
          ? "timeout"
          : "error";
        log(outcome, { reason: message });
        // Keep the authenticated seed so the UI stays interactive, but flag
        // the error so callers can render a retry.
        setState((prev) => ({
          ...prev,
          loading: false,
          isAuthed: true,
          // `message` stays in the log above; members only see plain copy.
          error: friendlyError(e, "We couldn't check your membership just now."),
        }));
      }
    }
    hydrateRef.current = hydrate;

    // Seed from the session the root already restored — no getUser() call.
    // If no synchronous session is visible yet, stay in the loading state
    // until INITIAL_SESSION/SIGNED_IN arrives or the bounded probe below
    // definitively proves this is a real guest. This prevents first-paint
    // guest/signup/dashboard flashes while auth is still hydrating.
    if (initial?.user?.id) {
      authResolved = true;
      markAuthResolved();
      hydrate(initial.user.id);
    }

    // SINGLE auth source: the root's AuthSync owns the one
    // `supabase.auth.onAuthStateChange` subscription and rebroadcasts every
    // event as `rt-auth-change`. This hook must never open its own
    // subscription — parallel subscribers contend on the auth LockManager and
    // can stall session restoration on mobile.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { event?: string; session?: { user?: { id?: string } } | null }
        | undefined;
      const evt = detail?.event;
      if (
        evt &&
        evt !== "SIGNED_IN" &&
        evt !== "SIGNED_OUT" &&
        evt !== "USER_UPDATED" &&
        evt !== "INITIAL_SESSION"
      ) {
        return;
      }
      const uid = detail?.session?.user?.id ?? null;
      if (uid) {
        authResolved = true;
        markAuthResolved();
        hydrate(uid);
      } else if (evt === "SIGNED_OUT") {
        // Explicit sign-out is always conclusive — clear member state at once.
        // Going through resolveNoSession() would return early for an already
        // resolved session, leaving name/member access stale until a reload.
        authResolved = true;
        markAuthResolved();
        lastUidRef.current = null;
        void hydrate(null);
      } else {
        resolveNoSession(0);
      }
    };
    window.addEventListener("rt-auth-change", handler);

    // Explicit refresh channel: fired right after a payment is confirmed
    // server-side (checkout success, reconciliation) so the member gets
    // access immediately without a reload or a sign-out/sign-in cycle.
    const refreshHandler = () => {
      const uid = lastUidRef.current;
      if (!uid) return;
      console.debug("[membership-activation] frontend refresh requested", {
        step: "frontend_access",
        uidHash: uid.slice(0, 8),
      });
      void hydrate(uid);
    };
    window.addEventListener("rt-membership-refresh", refreshHandler);

    // Bounded fallback. A slow or missing auth event must NEVER be read as
    // "signed out" — that is what bounced signed-in members to /auth. Instead
    // we ask Supabase for a definitive answer; only a real answer resolves
    // auth. If even that is unavailable, we stop the spinner, keep whatever
    // session state we have, and expose a retryable error while leaving
    // `authResolved` false so no page can redirect.
    // "No session" is only conclusive once. In the Lovable editor preview the
    // session is read from the editor over postMessage and that read can time
    // out, which looks identical to being signed out — an authenticated admin
    // was shown the members-only lock screen because of it. There, re-ask a
    // couple of times before concluding anyone is a guest.
    const resolveNoSession = (attempt: number) => {
      if (cancelled || authResolved) return;
      if (isBrokeredPreviewAuth() && attempt < 2) {
        window.setTimeout(() => void probeSession(attempt + 1), 1_200);
        return;
      }
      authResolved = true;
      markAuthResolved();
      hydrate(null);
    };

    const probeSession = async (attempt = 0) => {
      if (cancelled || authResolved) return;
      try {
        const { data } = await withTimeout(
          supabase.auth.getSession(),
          RESOLVE_TIMEOUT_MS,
          "Session lookup",
        );
        if (cancelled || authResolved) return;
        const uid = data?.session?.user?.id ?? null;
        if (!uid) {
          resolveNoSession(attempt);
          return;
        }
        authResolved = true;
        markAuthResolved();
        hydrate(uid);
      } catch {
        if (cancelled || authResolved) return;
        // Unresolved — not a guest. Leave authResolved false.
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            prev.error ??
            "We couldn't confirm your sign-in just now. Please try again.",
        }));
      }
    };

    // No stored session visible: ask Supabase straight away rather than
    // sitting on a spinner. The auth event may already have fired before this
    // hook mounted, so waiting for one would stall every page for seconds.
    let fallback: number | undefined;
    if (!initial?.user?.id) {
      void probeSession();
    } else {
      fallback = window.setTimeout(() => void probeSession(), 3_000);
    }

    return () => {
      cancelled = true;
      if (fallback !== undefined) window.clearTimeout(fallback);
      window.removeEventListener("rt-auth-change", handler);
      window.removeEventListener("rt-membership-refresh", refreshHandler);
      dropMembershipChannel();
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => {
    // Clear any prior error and show the loading state briefly while the
    // fresh hydrate() runs against a new timeout window.
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const uid = lastUidRef.current;
    if (uid) {
      void hydrateRef.current(uid);
      return;
    }
    // Auth itself never resolved — retry the definitive session lookup.
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        const id = data?.session?.user?.id ?? null;
        markAuthResolved();
        void hydrateRef.current(id);
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            "We couldn't confirm your sign-in just now. Please try again.",
        }));
      });
  }, [markAuthResolved]);

  return {
    ...state,
    hydrated,
    authResolved,
    canRedirectToSignIn:
      hydrated && authResolved && !state.isAuthed && !state.error,
    retry,
  };
}

